import React, { useMemo } from 'react';
import { AlertTriangle, AlertCircle, Info, X, CalendarCheck } from 'lucide-react';

/**
 * ReleaseConflictCallout
 *
 * Compares the Go-Live milestone date against Public Cloud release events
 * and surfaces actionable warnings above the Gantt. Pure derivation —
 * no API calls, no state side-effects.
 *
 * Severity rules:
 *   • CRITICAL — Go-Live falls inside a Maintenance window (±7 days)
 *   • WARNING  — Go-Live within 14 days BEFORE/AFTER a Prod release
 *               (upgrade-verification buffer recommended)
 *   • INFO     — Go-Live within 7 days of a Test release
 *
 * Multiple conflicts render as a stacked list; user can dismiss the
 * whole callout (purely visual — no persistence).
 *
 * Props:
 *   goLiveDate: 'YYYY-MM-DD' | undefined
 *   releases: [{id, quarter, release_type, date, label}]
 */
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function daysBetween(aIso, bIso) {
  const a = new Date(aIso + 'T00:00:00');
  const b = new Date(bIso + 'T00:00:00');
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
}

function shiftIsoByDays(iso, days) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Finds the smallest |Δ| ∈ [1, MAX_DAYS] whose shifted Go-Live has zero conflicts.
 * Tie-break: prefer LATER (positive shift) over EARLIER — PMs usually have less
 * runway behind them than ahead. Returns { shiftDays, newDate } or null if none.
 */
function findSafeShift(goLiveDate, releases, maxDays = 60) {
  if (!goLiveDate || !releases || !releases.length) return null;
  for (let absDelta = 1; absDelta <= maxDays; absDelta += 1) {
    for (const sign of [1, -1]) {
      const delta = sign * absDelta;
      const candidate = shiftIsoByDays(goLiveDate, delta);
      if (!candidate) continue;
      const conflicts = buildConflicts(candidate, releases);
      if (conflicts.length === 0) {
        return { shiftDays: delta, newDate: candidate };
      }
    }
  }
  return null;
}

function buildConflicts(goLiveDate, releases) {
  if (!goLiveDate || !releases || !releases.length) return [];
  const out = [];
  releases.forEach((evt) => {
    if (!evt?.date) return;
    const diff = daysBetween(goLiveDate, evt.date);
    if (diff === null) return;
    const absDiff = Math.abs(diff);
    const direction = diff === 0 ? 'on' : (diff > 0 ? 'after' : 'before');
    const quarter = evt.quarter || 'release';
    const label = evt.label || quarter;

    if (evt.release_type === 'maintenance' && absDiff <= 7) {
      out.push({
        severity: 'critical',
        evt,
        direction,
        absDiff,
        message: diff === 0
          ? `Go-Live falls ON the ${quarter} Maintenance window (${fmtDate(evt.date)}).`
          : `Go-Live is ${absDiff} day${absDiff === 1 ? '' : 's'} ${direction} the ${quarter} Maintenance window (${fmtDate(evt.date)}).`,
        suggestion: 'Avoid going live during maintenance — schedule before or after the window so customers experience the new release without infrastructure work overlapping.',
      });
    } else if (evt.release_type === 'prod' && absDiff <= 14) {
      out.push({
        severity: 'warning',
        evt,
        direction,
        absDiff,
        message: diff === 0
          ? `Go-Live falls ON the ${quarter} Production release (${fmtDate(evt.date)}).`
          : `Go-Live is ${absDiff} day${absDiff === 1 ? '' : 's'} ${direction} the ${quarter} Production release (${fmtDate(evt.date)}).`,
        suggestion: diff > 0
          ? 'Consider a 2-week buffer for upgrade verification — customers should validate the production release before going live.'
          : 'Production releases can introduce regressions — leave time for the upgrade to settle before launching.',
      });
    } else if (evt.release_type === 'test' && absDiff <= 7) {
      out.push({
        severity: 'info',
        evt,
        direction,
        absDiff,
        message: `Go-Live is ${absDiff === 0 ? 'on' : `${absDiff} day${absDiff === 1 ? '' : 's'} ${direction}`} the ${quarter} Test release (${fmtDate(evt.date)}).`,
        suggestion: 'Test releases are non-production — safe to proceed, but plan to validate against the upcoming production cut.',
      });
    }
  });
  // Sort: critical → warning → info, then by absDiff
  const order = { critical: 0, warning: 1, info: 2 };
  return out.sort((a, b) => order[a.severity] - order[b.severity] || a.absDiff - b.absDiff);
}

const COLORS = {
  critical: {
    border: 'border-red-300',
    bg: 'bg-red-50',
    text: 'text-red-900',
    sub: 'text-red-700',
    icon: AlertCircle,
    iconColor: 'text-red-600',
    label: 'Critical',
    chip: 'bg-red-600 text-white',
  },
  warning: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    sub: 'text-amber-700',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    label: 'Warning',
    chip: 'bg-amber-600 text-white',
  },
  info: {
    border: 'border-cyan-300',
    bg: 'bg-cyan-50',
    text: 'text-cyan-900',
    sub: 'text-cyan-700',
    icon: Info,
    iconColor: 'text-cyan-600',
    label: 'Info',
    chip: 'bg-cyan-600 text-white',
  },
};

export default function ReleaseConflictCallout({ goLiveDate, releases, onDismiss, onShiftGoLive }) {
  const conflicts = useMemo(() => buildConflicts(goLiveDate, releases), [goLiveDate, releases]);
  const safeShift = useMemo(
    () => (conflicts.length ? findSafeShift(goLiveDate, releases) : null),
    [conflicts.length, goLiveDate, releases]
  );

  if (!conflicts.length) return null;

  // Outer styling derives from the worst severity present.
  const top = conflicts[0].severity;
  const outer = COLORS[top];
  const Icon = outer.icon;

  const shiftLabel = safeShift
    ? `${safeShift.shiftDays > 0 ? '+' : ''}${safeShift.shiftDays} day${Math.abs(safeShift.shiftDays) === 1 ? '' : 's'}`
    : null;

  return (
    <div
      className={`mb-3 rounded-lg border ${outer.border} ${outer.bg} p-3`}
      data-testid="release-conflict-callout"
      data-severity={top}
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${outer.iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold ${outer.text}`}>
              Release calendar conflict{conflicts.length > 1 ? 's' : ''}
            </span>
            <span className={`text-[10px] uppercase tracking-wide font-bold rounded px-1.5 py-0.5 ${outer.chip}`}>
              {outer.label}
            </span>
            <span className={`text-[11px] ${outer.sub}`}>
              {conflicts.length} item{conflicts.length === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="space-y-1.5">
            {conflicts.map((c, i) => {
              const sub = COLORS[c.severity];
              return (
                <li
                  key={`${c.evt.id || c.evt.date}-${i}`}
                  className={`text-[12px] ${sub.text}`}
                  data-testid={`release-conflict-item-${c.severity}-${i}`}
                >
                  <strong>{c.message}</strong>
                  <div className={`text-[11px] mt-0.5 ${sub.sub}`}>
                    {c.suggestion}
                  </div>
                </li>
              );
            })}
          </ul>
          {safeShift && onShiftGoLive && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onShiftGoLive(safeShift.shiftDays, safeShift.newDate)}
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-md px-2.5 py-1.5 ${outer.chip} hover:opacity-90 transition-opacity`}
                data-testid="release-conflict-shift-btn"
                data-shift-days={safeShift.shiftDays}
                title={`Move Go-Live to ${fmtDate(safeShift.newDate)} — clears all conflicts`}
              >
                <CalendarCheck className="w-3.5 h-3.5" />
                Shift Go-Live {shiftLabel} → {fmtDate(safeShift.newDate)}
              </button>
              <span className={`text-[10px] ${outer.sub}`}>
                Smallest shift that clears every conflict.
              </span>
            </div>
          )}
          {!safeShift && conflicts.length > 0 && (
            <div className={`mt-2 text-[10px] ${outer.sub} italic`} data-testid="release-conflict-no-safe-shift">
              No conflict-free window found within ±60 days — review your release calendar or extend the search.
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className={`shrink-0 ${outer.iconColor} hover:opacity-70 p-0.5`}
            data-testid="release-conflict-dismiss"
            aria-label="Dismiss callout"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
